import React, { FC } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Stack,
  Typography,
  Skeleton
} from "@mui/material";
import { Badge } from "@vilocnv/allsetra-core";
import { MoreHoriz } from "@mui/icons-material";
import { round } from "lodash";
import moment from "moment";
import {
  OBJECT_RIDE_MODES,
  OBJECT_RIDE_MODES_COLOR,
  getObjectRideTotalTimeInMinutes,
  isOngoingRide,
  isManualRide
} from "app/data/helpers";
import {
  Clock,
  MileageIcon,
  MovingArrowLeft,
  PathLocationIcon,
  DriverIcon,
  keysActiveIcon as KeyIcon
} from "assets/icons";
import { AddressDisplay, getAddressInfo } from "./addressHelpers";

interface RideRowProps {
  row: any;
  index: number;
  selectedIds: Set<string>;
  selectableRows: boolean;
  onToggle: (id: string) => void;
  startAddress?: string;
  endAddress?: string;
  resolvedAddressLoading: boolean;
  loadingRideIds: Set<string>;
  showMovementLocal: boolean;
  cellActions: any[];
  canViewAddressForRide?: (ride: any) => boolean;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, row: any) => void;
  translator?: any;
  currentLocale: string;
}

const RideRow: FC<RideRowProps> = ({
  row,
  index,
  selectedIds,
  selectableRows,
  onToggle,
  startAddress,
  endAddress,
  resolvedAddressLoading,
  loadingRideIds,
  showMovementLocal,
  cellActions,
  canViewAddressForRide,
  onMenuOpen,
  translator: t,
  currentLocale
}) => {
  const id = row.uniqueId || row.id;
  const isLoading = loadingRideIds.has(id);
  const ongoing = isOngoingRide(row);

  const hasAvailableActions = cellActions.some((action) =>
    action.when ? action.when(row) : true
  );

  const startDateTimeLabel = moment(row.startDate)
    .locale(currentLocale)
    .format("MMM Do, YYYY, HH:mm");

  return (
    <Stack
      key={id}
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        py: 2,
        borderBottom: "1px solid #eee",
        ...(ongoing
          ? { borderLeft: "3px solid rgb(0, 178, 163)" }
          : { borderLeft: "3px solid transparent" })
      }}
    >
      {/* 1 - checkbox */}
      <Box sx={{ width: 40 }}>
        {selectableRows ? (
          <Checkbox
            checked={selectedIds.has(id)}
            onChange={() => onToggle(id)}
            size="small"
            disabled={ongoing}
          />
        ) : null}
      </Box>

      {/* 2 - date/time + ride type / tags */}
      <Box sx={{ flex: 2 }}>
        <Typography variant="body1" fontWeight={500}>
          {ongoing
            ? startDateTimeLabel
            : `${startDateTimeLabel} - ${moment(row.endDate)
                .locale(currentLocale)
                .format("HH:mm")}`}
          {row.startedWithoutIgnition && showMovementLocal ? (
            <MovingArrowLeft label={t ? t("common.movementRide") : undefined} />
          ) : null}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            mt: 2
          }}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={80} height={24} />
          ) : row.isCorrection ? (
            <Box
              sx={{
                backgroundColor: "#323946",
                border: "1px solid #76828F",
                borderRadius: "4px",
                padding: "4px 8px",
                color: "#FFFFFF",
                fontSize: "11px",
                fontWeight: 600
              }}
            >
              {t ? t("rideMode.correction") : "Correction"}
            </Box>
          ) : (
            <Badge
              colorScheme={OBJECT_RIDE_MODES_COLOR[row.rideMode] ?? "error"}
            >
              <div>
                {(t ? OBJECT_RIDE_MODES(t)[row.rideMode] : row.rideMode) ?? "-"}
              </div>
            </Badge>
          )}
          {isManualRide(row) ? (
            <Badge colorScheme={"info"} textVariant={"small"}>
              {t ? t("common.manualRide") : "MANUAL RIDE"}
            </Badge>
          ) : null}
          {/* Tags */}
          {!ongoing ? (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                ml: 1,
                alignItems: "center",
                "& div": {
                  border: "none !important"
                }
              }}
            >
              <Badge
                colorScheme={"blank"}
                icon={<Clock />}
                textVariant={"small"}
              >
                {`${getObjectRideTotalTimeInMinutes(row)} min`}
              </Badge>
              <Badge
                colorScheme={"blank"}
                icon={<MileageIcon />}
                textVariant={"small"}
              >
                {`${round(row.mileage || 0, 1)} km`}
              </Badge>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* Path Location */}
      <Box sx={{ flex: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 70,
              minWidth: 70,
              maxWidth: 70,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: 48
            }}
          >
            <Typography variant="body2" fontSize={14} lineHeight={1.2}>
              {moment(row.startDate).format("HH:mm")}
            </Typography>
            {ongoing ? (
              <Box
                title={t ? t("common.ongoingRide") : "Ongoing ride"}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#22C55E",
                  flexShrink: 0,
                  alignSelf: "flex-start",
                  mb: "3px",
                  animation: "ongoingPulse 2s ease-in-out infinite",
                  "@keyframes ongoingPulse": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.4, transform: "scale(0.85)" }
                  }
                }}
              />
            ) : (
              <Typography variant="body2" fontSize={14} lineHeight={1.2}>
                {moment(row.endDate).format("HH:mm")}
              </Typography>
            )}
          </Box>
          <Box sx={{ width: 28, textAlign: "center", flexShrink: 0 }}>
            <PathLocationIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: 48
              }}
            >
              {/* Start address */}
              <AddressDisplay
                address={startAddress}
                addressInfo={getAddressInfo(
                  row.startLatitude,
                  row.startLongitude,
                  row.rideMode
                )}
                isLoading={resolvedAddressLoading}
                userCanViewAddress={
                  canViewAddressForRide ? canViewAddressForRide(row) : true
                }
                rideMode={row.rideMode}
                translator={t}
              />

              {/* End address */}
              {ongoing ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2">
                    {t ? t("common.ongoingRide") : "Ongoing ride"}
                  </Typography>
                  <MovingArrowLeft
                    label={t ? t("common.ongoingRide") : undefined}
                  />
                </Stack>
              ) : (
                <AddressDisplay
                  address={endAddress}
                  addressInfo={getAddressInfo(
                    row.endLatitude,
                    row.endLongitude,
                    row.rideMode
                  )}
                  isLoading={resolvedAddressLoading}
                  userCanViewAddress={
                    canViewAddressForRide ? canViewAddressForRide(row) : true
                  }
                  rideMode={row.rideMode}
                  translator={t}
                />
              )}
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Driver or Key */}
      <Box sx={{ flex: 1 }}>
        {isLoading ? (
          <Skeleton variant="text" width={100} height={20} />
        ) : row.driver?.firstName ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <DriverIcon style={{ width: 16, height: 16, color: "#d5d9e8" }} />
            <Typography variant="body2">
              {`${String(row.driver?.firstName)} ${String(
                row.driver?.lastName || ""
              )}`}
            </Typography>
          </Stack>
        ) : row.key?.label || row.keyId || row.rawKeyId ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <KeyIcon style={{ width: 16, height: 16, color: "#d5d9e8" }} />
            <Typography variant="body2">
              {row.key?.label || row.keyId || row.rawKeyId || "-"}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2">-</Typography>
        )}
      </Box>

      {/* Action */}
      <Box>
        <IconButton
          onClick={(e) => onMenuOpen(e, row)}
          disabled={!hasAvailableActions}
          sx={{
            opacity: hasAvailableActions ? 1 : 0.3,
            cursor: hasAvailableActions ? "pointer" : "not-allowed"
          }}
        >
          <MoreHoriz />
        </IconButton>
      </Box>
    </Stack>
  );
};

export default RideRow;
